import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, MapPin, Save, AlertCircle, Plus, Minus } from 'lucide-react';
import { getBrandList, BrandNode } from '../../../utils/brand';
import {
  getBrandMemberships,
  saveMembershipBrandsSync,
  type MembershipBrandSyncItem,
} from '../../../utils/membership';

interface BrandTreeNode {
  code: string;
  name: string;
  depth: number;
  children?: BrandTreeNode[];
}

const convertBrandNode = (node: BrandNode): BrandTreeNode => ({
  code: node.code,
  name: node.name,
  depth: node.depth,
  children:
    node.subBrands && node.subBrands.length > 0
      ? node.subBrands.map(convertBrandNode)
      : undefined,
});

export interface MembershipBrandMappingTabProps {
  projectCode: string;
  membershipCode: string;
  /** 현재 멤버십 타입 (sync API 필수) */
  membershipType: string;
  /** 현재 리워드 방식 (sync API 필수. REWARD면 STAMP|POINT) */
  rewardType: string;
  /** 부모에서 이미 조회한 경우 전달 (getBrandMemberships 중복 호출 방지) */
  parentBrandCodes?: string[] | null;
  onSaveSuccess: (newBrandCount?: number, newBrandCodes?: string[]) => void;
}

const MembershipBrandMappingTab: React.FC<MembershipBrandMappingTabProps> = ({
  projectCode,
  membershipCode,
  membershipType,
  rewardType,
  parentBrandCodes,
  onSaveSuccess,
}) => {
  const [treeData, setTreeData] = useState<BrandTreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [initialBrandCodes, setInitialBrandCodes] = useState<Set<string>>(new Set());
  const [checkedBrandCodes, setCheckedBrandCodes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 1. 브랜드 트리 목록 조회 2. getBrandMemberships로 현재 매핑된 브랜드만 체크 표시
  useEffect(() => {
    if (parentBrandCodes === null) return;

    const ac = new AbortController();
    let ignore = false;

    const load = async () => {
      if (!projectCode || !membershipCode) return;
      try {
        setIsLoading(true);
        setError(null);

        const useParentMapping = Array.isArray(parentBrandCodes);
        const [brandRes, mappingRes] = await Promise.all([
          getBrandList(projectCode, undefined, ac.signal),
          useParentMapping
            ? Promise.resolve({ brandMemberships: [] as { brandCode: string }[] })
            : getBrandMemberships(projectCode, membershipCode),
        ]);
        if (ignore) return;

        const nodes = brandRes.brands.map(convertBrandNode);
        setTreeData(nodes);
        const codes = useParentMapping
          ? (parentBrandCodes as string[])
          : (mappingRes?.brandMemberships ?? []).map((b) => b.brandCode).filter(Boolean);
        const initial = new Set(codes);
        setInitialBrandCodes(initial);
        setCheckedBrandCodes(initial);
        const allIds = new Set<string>();
        const collect = (n: BrandTreeNode[]) => {
          n.forEach((node) => {
            allIds.add(node.code);
            if (node.children?.length) collect(node.children!);
          });
        };
        collect(nodes);
        setExpandedNodes(allIds);
      } catch (e) {
        const isAborted =
          (e as any)?.name === 'AbortError' ||
          (e instanceof Error && /abort/i.test(e.message));
        if (ignore || isAborted) return;
        setError(e instanceof Error ? e.message : '데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    load();
    return () => {
      ignore = true;
      ac.abort();
    };
  }, [projectCode, membershipCode, parentBrandCodes]);

  const toggleExpand = (code: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // code → name 매핑 (변경 현황 표시용)
  const codeToName = useMemo(() => {
    const map = new Map<string, string>();
    const collect = (nodes: BrandTreeNode[]) => {
      nodes.forEach((n) => {
        map.set(n.code, n.name);
        if (n.children?.length) collect(n.children);
      });
    };
    collect(treeData);
    return map;
  }, [treeData]);

  const addedCodes = useMemo(
    () => [...checkedBrandCodes].filter((c) => !initialBrandCodes.has(c)),
    [checkedBrandCodes, initialBrandCodes]
  );
  const removedCodes = useMemo(
    () => [...initialBrandCodes].filter((c) => !checkedBrandCodes.has(c)),
    [initialBrandCodes, checkedBrandCodes]
  );
  const hasChanges = addedCodes.length > 0 || removedCodes.length > 0;

  const toggleCheck = (code: string) => {
    setCheckedBrandCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      setIsSubmitting(true);
      const addedCodes = [...checkedBrandCodes].filter((c) => !initialBrandCodes.has(c));
      const removedCodes = [...initialBrandCodes].filter((c) => !checkedBrandCodes.has(c));
      const brands: MembershipBrandSyncItem[] = [
        ...addedCodes.map((brandCode): MembershipBrandSyncItem => ({
          brandCode,
          membershipCode,
          membershipType,
          rewardType,
          operation: 'ADD',
        })),
        ...removedCodes.map((brandCode): MembershipBrandSyncItem => ({
          brandCode,
          membershipCode,
          membershipType,
          rewardType,
          operation: 'DELETE',
        })),
      ];
      if (brands.length > 0) {
        await saveMembershipBrandsSync(projectCode, membershipCode, { brands });
      }
      setInitialBrandCodes(new Set(checkedBrandCodes));
      onSaveSuccess(checkedBrandCodes.size, [...checkedBrandCodes]);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderNode = (node: BrandTreeNode, depth: number) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.code);
    const isChecked = checkedBrandCodes.has(node.code);

    return (
      <div key={node.code} className="select-none">
        <div
          className="flex items-center gap-2 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => hasChildren && toggleExpand(node.code)}
        >
          <span className="w-5 flex-shrink-0">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )
            ) : (
              <span className="w-4 inline-block" />
            )}
          </span>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(ev) => {
              ev.stopPropagation();
              toggleCheck(node.code);
            }}
            onClick={(ev) => ev.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-900">{node.name}</span>
          <span className="text-xs text-gray-400 font-mono">({node.code})</span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasChanges && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 mb-3">변경 현황</p>
          <div className="flex flex-col sm:flex-row gap-4 text-sm">
            {addedCodes.length > 0 && (
              <div className="flex-1">
                <span className="inline-flex items-center gap-1 text-green-700 font-medium mb-1">
                  <Plus className="w-4 h-4" />
                  추가됨 ({addedCodes.length}개)
                </span>
                <ul className="space-y-0.5 text-green-600">
                  {addedCodes.map((code) => (
                    <li key={code}>
                      {codeToName.get(code) || code} <span className="font-mono text-gray-500">({code})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {removedCodes.length > 0 && (
              <div className="flex-1">
                <span className="inline-flex items-center gap-1 text-red-700 font-medium mb-1">
                  <Minus className="w-4 h-4" />
                  삭제됨 ({removedCodes.length}개)
                </span>
                <ul className="space-y-0.5 text-red-600">
                  {removedCodes.map((code) => (
                    <li key={code}>
                      {codeToName.get(code) || code} <span className="font-mono text-gray-500">({code})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            브랜드 매핑
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            멤버십에 연결할 브랜드를 체크박스로 선택하세요. 저장 후 교환권 설정 탭으로 이동합니다.<br/>
            하위 브랜드를 체크하지 않으면 하위 브랜드는 멤버십에 연결되지 않습니다.
          </p>
        </div>
        <div className="px-4 lg:px-6 py-4 max-h-[400px] overflow-y-auto">
          {treeData.length === 0 ? (
            <p className="text-sm text-gray-500">등록된 브랜드가 없습니다.</p>
          ) : (
            treeData.map((node) => renderNode(node, 0))
          )}
        </div>
      </div>

      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {hasChanges && (
            <p className="text-sm text-amber-600 flex-1">
              저장하지 않은 변경사항이 {addedCodes.length + removedCodes.length}건 있습니다.
            </p>
          )}
          <div className="flex justify-end sm:ml-auto">
            <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
          >
            <Save className="w-5 h-5 mr-2" />
            {isSubmitting ? '저장 중...' : '저장 후 교환권 설정 진행'}
          </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MembershipBrandMappingTab;
